import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { DslService, MenuContentItem, MenuItemsService, MenuSection, NavigationService, ProductService } from '@frontend/vanilla/core';
import { MenuItemHighlightService } from '@frontend/vanilla/shared/menu-item';
import { PLATFORM, linkedTransferState, tapIfServer } from '@frontend/vanilla/ssr';
import { BehaviorSubject, Observable, combineLatest, filter, from, of, shareReplay, skipUntil, startWith, switchMap, take } from 'rxjs';

import { BottomNavConfig } from './bottom-nav.client-config';

export interface BottomNavControlEvent {
    state: 'show' | 'hide';
}

/**
 * @whatItDoes Provides functionality to manipulate the bottom nav
 *
 * @description
 *
 * # Overview
 *
 * This service provides functionality for manipulating the bottom nav:
 *  - Show and hide (once, until the state is set by scrolling)
 *  - Set counter for an item
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class BottomNavService {
    private menuItemsService = inject(MenuItemsService);
    private bottomNavConfig = inject(BottomNavConfig);
    private dslService = inject(DslService);
    private navigationService = inject(NavigationService);
    private menuItemHighlightingService = inject(MenuItemHighlightService);
    private productService = inject(ProductService);

    currentHighlightedProductName: string | null = null;

    private controlEvents = new BehaviorSubject<BottomNavControlEvent>({ state: 'show' });
    private _enableHighlighting = true;
    private readonly platform = inject(PLATFORM);
    evaluatedItemsTransferState = linkedTransferState<MenuContentItem[]>('bottom-nav-items', []);
    private waitForCurrentProduct$ = this.productService.productChanged.pipe((o$) =>
        this.productService.current.name === 'host' ? o$ : o$.pipe(startWith(this.productService.current)),
    );
    evaluatedItems$ = combineLatest([
        from(this.bottomNavConfig.whenReady),
        // only when coming from SSR we wait until the product was registered until we emit items
        this.platform.isServer || this.platform.isServerRendered ? this.waitForCurrentProduct$ : of(null),
    ]).pipe(
        take(1),
        switchMap(() => this.dslService.evaluateContent(this.bottomNavConfig.items)),
        tapIfServer((items) => this.evaluatedItemsTransferState.set(items)),
        startWith(this.evaluatedItemsTransferState()),
        filter((items) => items.length > 0),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    evaluatedItems = toSignal(this.evaluatedItems$);

    /** @internal */
    get inputEvents(): Observable<BottomNavControlEvent> {
        return this.controlEvents;
    }

    initProductHighlighting() {
        this.menuItemHighlightingService.initHighlighting(this.bottomNavConfig.items);
        this.navigationService.locationChange.subscribe(() => this._enableHighlighting && this.setHighlightedProduct());
    }

    set enableHighlighting(value: boolean) {
        this._enableHighlighting = value;
    }

    /** Shows the bottom nav if it is currently hidden. */
    show() {
        this.controlEvents.next({ state: 'show' });
    }

    /** Hides the bottom nav if it is currently shown. */
    hide() {
        this.controlEvents.next({ state: 'hide' });
    }

    /** Sets and item in the bottom nav to active state. */
    setActiveItem(itemName: string) {
        this.menuItemsService.setActive(MenuSection.BottomNav, itemName);
    }

    /** Assigns a number to be shown as a counter for a bottom nav item. */
    setItemCounter(itemName: string, count: any, cssClass?: string) {
        this.menuItemsService.setCounter(MenuSection.BottomNav, itemName, count, cssClass);
    }

    /** Gets the enablement status of the bottom nav. */
    isEnabled(): Observable<boolean> {
        return this.dslService.evaluateExpression<boolean>(this.bottomNavConfig.isEnabledCondition).pipe(skipUntil(this.bottomNavConfig.whenReady));
    }

    setHighlightedProduct() {
        const product = this.menuItemHighlightingService.setHighlightedProduct(this.bottomNavConfig.items, this.currentHighlightedProductName);

        if (product?.name) {
            this.menuItemHighlightingService.setActiveItem(MenuSection.BottomNav, product.name);
        }
    }
}
